import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { filter, Observable, Subject, takeUntil } from 'rxjs';
import { StandingAuthorizationsTableActions, TransactionsTableActions } from '../../../create-request/actions';
import { NicknameOperation } from '../../models/Nickname';
import { selectNicknameCallSucceeded } from '../../reducers';
import { ValidateInputService } from '../../services/validate-input.service';
import { nicknameAllowedCharacters } from '../../utilities/regex-patterns';
import { selectTemplateNickNameCallSuccessful } from '../../../account-info-header/reducers';

@Component({
  standalone: false,
  selector: 'mm-sloa-nickname',
  templateUrl: './sloa-nickname.component.html',
  styleUrls: ['./sloa-nickname.component.scss'],
})
export class SloaNicknameComponent implements OnInit, OnDestroy {
  @ViewChild('nicknameInput') input: ElementRef;
  params: any;
  isRecurring: boolean = false;
  editClicked: boolean = false;
  addNickNameClicked: boolean = false;
  sloaNickNameForm: FormGroup;
  nickNameInContext: string;
  nicknameAllowedCharacters = nicknameAllowedCharacters;
  isStandingInstruction: boolean = false;
  isAchRecurringProfile: boolean;
  hasNicknameOption: boolean;
  isTemplate: boolean;
  nicknameCallSuccess$: Observable<boolean>;
  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store,
    public validationService: ValidateInputService,
  ) {}

  agInit(params: any) {
    this.params = params;
  }

  ngOnInit(): void {
    this.sloaNickNameForm = this.formBuilder.group({
      nickname: this.params.data.Nickname,
    });
    this.isStandingInstruction = this.params.data.isStandingInstruction;
    this.nickNameInContext = this.nickNameFormControl.value ?? '';

    this.isRecurring = this.params.data.isRecurring;
    this.isTemplate = this.params.data.isTemplate;
    this.isAchRecurringProfile = this.isRecurring && this.params.data.Type.includes('ACH');
    this.hasNicknameOption = this.isAchRecurringProfile || !this.isRecurring;
    this.nicknameCallSuccess$ = this.isTemplate ? this.store.select(selectTemplateNickNameCallSuccessful) : this.store.select(selectNicknameCallSucceeded);

    this.nicknameCallSuccess$
      .pipe(
        takeUntil(this.destroy$),
        filter((val) => val)
      )
      .subscribe((_) => {
        this.params.data.Nickname = this.nickNameInContext;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  onMouseDown(event: MouseEvent) {
    let mainButtonPressed = event.button === 0;
    if (mainButtonPressed) {
      event.preventDefault();
    }
  }

  onSubmit(): void {
    this.editClicked = this.addNickNameClicked = false;
    let trimmedNickName = this.nickNameFormControl.value.trim();
    if (
      (this.nickNameFormControl.value.length > 0 && trimmedNickName === '') ||
      trimmedNickName === this.nickNameInContext
    ) {
      this.nickNameFormControl.setValue(this.nickNameInContext);
      return;
    }

    this.nickNameFormControl.setValue(trimmedNickName);
    if (this.isTemplate) {
      this.store.dispatch(TransactionsTableActions.addUpdateDeleteNicknameTemplates({ draftId: this.params.data.DraftId, nickName: trimmedNickName }));
    } else {
      this.store.dispatch(
        StandingAuthorizationsTableActions.addUpdateDeleteNickname({
          rowData: {
            nickname: trimmedNickName,
            transactionType: this.params.data.Type,
            standingAuthId: this.params.data.StandingAuthId,
          },
          requestType: this.nicknameOperation,
        })
      );
    }
    this.nickNameInContext = trimmedNickName;
  }

  onCancel(): void {
    this.editClicked = this.addNickNameClicked = false;
    this.nickNameFormControl.setValue(this.nickNameInContext);
  }

  onEditClicked(): void {
    this.editClicked = true;
    this.setFocusOnInput();
  }

  onAddNickname(): void {
    this.addNickNameClicked = true;
    this.setFocusOnInput();
  }

  onInput(el: HTMLInputElement) {
    let reg: RegExp = /[^a-zA-Z0-9 #$%&'*]/g;
    let trimmedText = el.value.replace(reg, '');
    this.nickNameFormControl.setValue(trimmedText);
  }

  setFocusOnInput() {
    setTimeout(() => {
      this.input.nativeElement.focus();
    });
  }

  selectAll(el: HTMLInputElement) {
    el.select();
  }

  get containsNickname(): boolean {
    return !!this.nickNameFormControl.value;
  }

  get showNicknameWithEdit(): boolean {
    return (
      this.containsNickname && !this.editClicked && !this.addNickNameClicked
    );
  }

  get showAddNicknameOption(): boolean {
    return (
      !this.editClicked && !this.containsNickname && !this.addNickNameClicked
        && (this.isStandingInstruction || this.isAchRecurringProfile || this.isTemplate)
    );
  }

  get nickNameFormControl() {
    return this.sloaNickNameForm.controls.nickname;
  }

  get nicknameOperation() {
    if (!!this.params.data.Nickname) {
      if (!!this.nickNameFormControl.value) {
        return NicknameOperation.Edit;
      } else {
        return NicknameOperation.Delete;
      }
    } else {
      return NicknameOperation.Add;
    }
  }
}
